// WHAT: Hook for managing Supabase configuration with loading and refetch
// WHY:  Provides interface for loading and refreshing Supabase configuration
// DOMAIN: Settings → Supabase
// DEPENDENCIES: core.invoke for backend commands
// RISKS:
//   - NOTE: Error handling is silent - may not provide user feedback
//   - NOTE: Reload mechanism uses counter - may cause unnecessary re-renders

import { core } from "@tauri-apps/api";
import { useEffect, useState, useCallback } from "react";

import { type SupabaseConfig } from "../types/SupabaseConfig";

/**
 * WHAT:
 *   Hook that provides interface for loading and managing Supabase configuration
 *   with refetch capability.
 *
 * WHY:
 *   Centralizes Supabase configuration loading logic and provides consistent
 *   interface for components.
 *
 * BEHAVIOR:
 *   - Loads configuration on mount
 *   - Supports manual refetch
 *   - Manages loading state
 *   - Returns configuration or null
 *
 * RISKS:
 *   - NOTE: Error handling is silent - may not provide user feedback
 *   - NOTE: Reload mechanism uses counter - may cause unnecessary re-renders
 *
 * @returns {{ config: SupabaseConfig | null; loading: boolean; refetch: () => void }} Configuration object with loading state and refetch function
 *
 */
export function useSupabaseConfig() {
  const [config, setConfig] = useState<null | SupabaseConfig>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const refetch = useCallback(() => {
    setLoading(true);
    setReload((c) => c + 1);
  }, []);

  useEffect(() => {
    core
      .invoke<[string, string]>("get_supabase_config_cmd")
      .then(([url, key]) => {
        setConfig({ key, url });
      })
      .catch(() => {
        setConfig(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [reload]);

  return { config, loading, refetch };
}
